// A tiered cache for everything the app fetches, keyed on how fast each kind
// of data actually changes rather than on a clock alone: terroir figures move
// with a backend import (weeks), wine scores and reviews move daily, the
// user's own notes move whenever they tap save. An entry is served at once
// when present and refreshed behind it when its policy says so, so a screen
// paints from the phone first and the network only ever improves it. Storage
// is the asset cache (IndexedDB, with a per-session Map where that is missing);
// lib/useCached.ts is the same thing as a hook.
import { readAsset, removeAssetsWhere, removeAssetsWithPrefix, writeAsset } from "./assetCache";

export interface CachePolicy {
  /** Folded into every entry; bump it when the data's shape or meaning changes. */
  readonly version: string;
  /** Served without a network round-trip while younger than this; older entries are served and refreshed behind. */
  readonly freshMs: number;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const POLICY = {
  /** Soil, climate normals, elevation bands, boundaries, region footprints and vintage verdicts: a backend import changes them, on the order of weeks. */
  terroir: { version: "druif-1", freshMs: 30 * DAY },
  /** Wines, matches and searches: scores and reviews move, so an hour before asking again. */
  wine: { version: "druif-1", freshMs: HOUR },
  /** The user's own notes, cellar and progress on the edge: served at once, refreshed on every visit. */
  user: { version: "edge-1", freshMs: 0 },
} as const satisfies Record<string, CachePolicy>;

/** Entries nobody has asked for in this long are swept at start-up, so searches from months ago do not pile up. */
export const PRUNE_AFTER_MS = 90 * DAY;

export type Guard<T> = (value: unknown) => value is T;

export interface Entry<T> {
  fetchedAt: number;
  data: T;
}

function entryGuard<T>(is: Guard<T>): Guard<Entry<T>> {
  return (value): value is Entry<T> =>
    typeof value === "object" &&
    value !== null &&
    "fetchedAt" in value &&
    typeof value.fetchedAt === "number" &&
    "data" in value &&
    is(value.data);
}

const isAnyEntry = entryGuard((value): value is unknown => value !== undefined);

/**
 * Trusts any object as `T`, for druif records this app wrote to the cache
 * itself and reads back; a shape check would add nothing a stale schema
 * did not already pass on the way in.
 */
export function objectGuard<T extends object>(): Guard<T> {
  return (value): value is T => typeof value === "object" && value !== null;
}

/** A zod-style schema (anything with `safeParse`) as a guard, for the edge's contract types. */
export function zodGuard<T>(schema: {
  safeParse: (value: unknown) => { success: boolean };
}): Guard<T> {
  return (value): value is T => schema.safeParse(value).success;
}

/** The stored entry for `key` under this policy's version, for `useCached`; screens call `cachedFetch`. */
export function readEntry<T>(
  key: string,
  policy: CachePolicy,
  is: Guard<T>,
): Promise<Entry<T> | undefined> {
  return readAsset(key, policy.version, entryGuard(is));
}

/** Stores `data` as fetched now, for `useCached`. */
export function writeEntry(key: string, policy: CachePolicy, data: unknown): Promise<void> {
  const entry: Entry<unknown> = { fetchedAt: Date.now(), data };
  return writeAsset(key, policy.version, entry);
}

export function isFresh(entry: Entry<unknown>, policy: CachePolicy, now = Date.now()): boolean {
  return now - entry.fetchedAt < policy.freshMs;
}

/**
 * The value for `key`: fresh from the cache, else stale from the cache with a
 * refresh behind it (the next read sees the new value), else fetched and
 * stored. Rejects only when nothing is cached and the loader fails.
 */
export async function cachedFetch<T>(
  key: string,
  policy: CachePolicy,
  loader: () => Promise<T>,
  is: Guard<T>,
): Promise<T> {
  const entry = await readEntry(key, policy, is);
  if (entry !== undefined) {
    if (isFresh(entry, policy)) return entry.data;
    loader()
      .then((data) => writeEntry(key, policy, data))
      .catch(() => undefined);
    return entry.data;
  }
  const data = await loader();
  await writeEntry(key, policy, data);
  return data;
}

/** Drops every entry whose key starts with `prefix`, after a write that made them wrong. */
export function invalidate(prefix: string): Promise<void> {
  return removeAssetsWithPrefix(prefix);
}

/** Drops every entry fetched longer ago than `maxAgeMs`; assets without a fetch time (outlines) stay. */
export function pruneEntries(maxAgeMs = PRUNE_AFTER_MS): Promise<void> {
  const cutoff = Date.now() - maxAgeMs;
  return removeAssetsWhere((row) => isAnyEntry(row.value) && row.value.fetchedAt < cutoff);
}
